import {
    AzureSqlMILinkedService,
    IModel,
    IAttribute,
    IViolation,
    LinkedService,
    ManagedIntegrationRuntime,
    ModelConstants,
    PackageStore,
    ExecuteSSISPackageChildPackageConfig,
    SSISActivity,
    PatternValidatorFactoryService
} from 'shell-models-lib';
import { Metamodel } from 'app/model/base/model';

import { IntegrationRuntime } from 'app/model/integrationRuntime/integrationRuntime';
import { format, sanitizeAndFormatString, stringIsNullOrEmpty } from 'app/shared/utils/string.util';
import { ValueAdapter } from '../adapters/value.adapter';
import { Pipeline } from './pipeline';

@Metamodel.model({
    name: ModelConstants.executeSSISPackageActivity,
    display: [
        format(stringResources.ModelName_ActivityTemplate, stringResources.SsisActivity_DisplayText),
        format(stringResources.ModelName_ActivitiesTemplate, stringResources.SsisActivity_DisplayText)
    ],
    referable: true,
    disabled: __IS_FABRIC_DPE__
})
export class ExecuteSSISPackageActivity extends SSISActivity {
    public static readonly MODEL: IModel;
    public static readonly PACKAGE_PATH: IAttribute;
    public static readonly SSIS_IR: IAttribute;
    public static readonly RUNTIME: IAttribute;
    public static readonly ENVIRONMENT_PATH: IAttribute;
    public static readonly SSIS_FOLDER: IAttribute;
    public static readonly SSIS_PROJECT: IAttribute;
    public static readonly SSIS_PACKAGE: IAttribute;
    public static readonly SSIS_ENVIRONMENT: IAttribute;
    public static readonly WINAUTH_DOMAIN: IAttribute;
    public static readonly WINAUTH_USERNAME: IAttribute;
    public static readonly WINAUTH_PASSWORD: IAttribute;

    public static readonly PACKAGE_ACCESS_DOMAIN: IAttribute;
    public static readonly PACKAGE_ACCESS_USERNAME: IAttribute;
    public static readonly PACKAGE_ACCESS_PASSWORD: IAttribute;
    public static readonly CONFIG_ACCESS_DOMAIN: IAttribute;
    public static readonly CONFIG_ACCESS_USERNAME: IAttribute;
    public static readonly CONFIG_ACCESS_PASSWORD: IAttribute;
    public static readonly FILE_PACKAGE_PATH: IAttribute;
    public static readonly LOG_ACCESS_DOMAIN: IAttribute;
    public static readonly LOG_ACCESS_USERNAME: IAttribute;
    public static readonly LOG_ACCESS_PASSWORD: IAttribute;

    public static readonly INLINE_PACKAGE_NAME: IAttribute;
    public static readonly PACKAGE_CONTENT: IAttribute;
    public static readonly PACKAGE_LAST_MODIFIED_DATE: IAttribute;
    public static readonly CHILD_PACKAGES: IAttribute;
    public static readonly SQLSERVER_LINKEDSERVICE: IAttribute;
    public static readonly PACKAGE_STORE_NAME: IAttribute;
    public static readonly PACKAGE_STORE_PATH: IAttribute;

    public static readonly CUSTOMIZED_LOGGING_LEVEL_PREFIX = 'CUSTOMIZED:';

    public static readonly VALUE_TYPE_REFERENCE = 'R';

    public static readonly CONFIGURATION_MODE_AUTO = 'auto';
    public static readonly CONFIGURATION_MODE_MANUAL = 'manual';
    public static readonly TRANSIENT_STATUS_NONE = 'none';
    public static readonly TRANSIENT_STATUS_REFRESH = 'refreshing';
    public static readonly TRANSIENT_STATUS_INIT = 'loading';
    public static readonly PARAMETER_TRANSIENT_STATUS_NONE = 'none';
    public static readonly PARAMETER_TRANSIENT_STATUS_INIT = 'loading';
    public static readonly PACKAGE_LOCATION_SSISDB = 'SSISDB';
    public static readonly PACKAGE_LOCATION_FILE = 'File';
    public static readonly PACKAGE_LOCATION_INLINE_PACKAGE = 'InlinePackage';
    public static readonly PACKAGE_LOCATION_SQL_SERVER = 'Sql';
    public static readonly PACKAGE_LOCATION_PACKAGE_STORE = 'PackageStore';
    public static readonly WINAUTH_PROPERTY_GROUP_SIZE = 3;
    public static readonly PACKAGE_ACCESS_CREDENTIALS_GROUP_SIZE = 3;
    public static readonly CONFIG_ACCESS_CREDENTIALS_GROUP_SIZE = 3;

    public static readonly CONFIGURATION_EXTENSION = '.dtsConfig';

    // All inline packages in activities should not over 250K
    public static readonly INLINE_PACKAGE_ACTIVITY_MAX_SIZE_KB = 250;

    // All inline packages in activites and pipeline parameters should not over 700K
    public static readonly INLINE_PACKAGE_PIPELINE_MAX_SIZE_KB = 700;

    public static readonly INLINE_PACKAGE_COMPRESSION_HEADER_V1 = 'VER001_';
    public static readonly INLINE_PACKAGE_BUFFER_FORMAT_V1 = 'base64';
    public static readonly INLINE_PACKAGE_FILE_NAME_SEPARATOR = '|';
    public static readonly INLINE_PACKAGE_TIMEZONE = 'UTC';

     private static readonly PACKAGESTORE_PACKAGEPATH_VALIDATOR =
        new PatternValidatorFactoryService().createValidator(/^([^\\/:*?"<>|\r\n]+[\/\\]{1})*[^\\/:*?"<>|\r\n]*$/, stringResources.InvalidValue);

    public configurationMode: string = ExecuteSSISPackageActivity.CONFIGURATION_MODE_AUTO;
    public transientStatus: string = ExecuteSSISPackageActivity.TRANSIENT_STATUS_NONE;
    public parameterTransientStatus: string = ExecuteSSISPackageActivity.PARAMETER_TRANSIENT_STATUS_NONE;

    @Metamodel.attribute({
        display: stringResources.RuntimeDisplayName,
        association: 'RUNTIME',
        templatable: true
    })
    public runtime?: 'x86' | 'x64';

    public override packageLocation: string = ExecuteSSISPackageActivity.PACKAGE_LOCATION_SSISDB;

    @Metamodel.attribute({
        display: stringResources.DomainLabel,
        association: 'WINAUTH_DOMAIN',
        templatable: true,
        constraint: {
            validator: ExecuteSSISPackageActivity.createWinauthDomainValidator()
        }
    })
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public winauthDomain: any;

    @Metamodel.attribute({
        display: stringResources.ModelName_UserName,
        association: 'WINAUTH_USERNAME',
        templatable: true,
        constraint: {
            validator: ExecuteSSISPackageActivity.createWinauthUserNameValidator()
        }
    })
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public winauthUserName: any;

    @Metamodel.attribute({
        display: stringResources.ModelName_Password,
        association: 'WINAUTH_PASSWORD',
        templatable: true,
        constraint: {
            validator: ExecuteSSISPackageActivity.createWinauthPasswordValidator()
        }
    })
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public winauthPassword: any;

    public useWinauth: boolean;

    @Metamodel.attribute({
        display: stringResources.ModelName_SqlServerLinkedService,
        association: 'SQLSERVER_LINKEDSERVICE',
        templatable: false,
        reference: true,
        type: LinkedService.MODEL,
        constraint: {
            validator: ExecuteSSISPackageActivity.createSqlServerLinkedServiceValidator()
        }
    })
    public sqlServerLinkedService: LinkedService;

    @Metamodel.attribute({
        display: stringResources.SsisActivityPackagePathLabel,
        association: 'PACKAGE_PATH',
        templatable: true,
        constraint: {
            validator: ExecuteSSISPackageActivity.createPackagePathValidator()
        }
    })
    public packagePath: string;

    @Metamodel.attribute({
        display: stringResources.SsisActivityPackagePathLabel,
        association: 'FILE_PACKAGE_PATH',
        templatable: true,
        constraint: {
            validator: ExecuteSSISPackageActivity.createFilePackagePathValidator()
        }
    })
    public filePackagePath: string;

    @Metamodel.attribute({
        display: stringResources.SsisActivityPackageStoreNameLabel,
        association: 'PACKAGE_STORE_NAME',
        templatable: false,
        constraint: {
            validator: ExecuteSSISPackageActivity.createPackageStoreNameValidator()
        }
    })
    public packageStoreName: string;

    @Metamodel.attribute({
        display: stringResources.SsisActivityPackagePathLabel,
        association: 'PACKAGE_STORE_PATH',
        templatable: true,
        constraint: {
            validator: ExecuteSSISPackageActivity.createPackageStorePathValidator()
        }
    })
    public packageStorePath: string;

    @Metamodel.attribute({
        display: stringResources.DomainLabel,
        association: 'PACKAGE_ACCESS_DOMAIN',
        templatable: true,
        constraint: {
            validator: ExecuteSSISPackageActivity.createPackageDomainValidator()
        }
    })
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public packageAccessDomain: any;

    @Metamodel.attribute({
        display: stringResources.ModelName_UserName,
        association: 'PACKAGE_ACCESS_USERNAME',
        templatable: true,
        constraint: {
            validator: ExecuteSSISPackageActivity.createPackageUserNameValidator()
        }
    })
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public packageAccessUserName: any;

    @Metamodel.attribute({
        display: stringResources.ModelName_Password,
        association: 'PACKAGE_ACCESS_PASSWORD',
        templatable: true,
        constraint: {
            validator: ExecuteSSISPackageActivity.createPackagePasswordValidator()
        }
    })
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public packageAccessPassword: any;

    @Metamodel.attribute({
        display: stringResources.DomainLabel,
        association: 'CONFIG_ACCESS_DOMAIN',
        templatable: true,
        constraint: {
            validator: ExecuteSSISPackageActivity.createConfigDomainValidator()
        }
    })
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public configAccessDomain: any;

    @Metamodel.attribute({
        display: stringResources.ModelName_UserName,
        association: 'CONFIG_ACCESS_USERNAME',
        templatable: true,
        constraint: {
            validator: ExecuteSSISPackageActivity.createConfigUserNameValidator()
        }
    })
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public configAccessUserName: any;

    @Metamodel.attribute({
        display: stringResources.ModelName_Password,
        association: 'CONFIG_ACCESS_PASSWORD',
        templatable: true,
        constraint: {
            validator: ExecuteSSISPackageActivity.createConfigPasswordValidator()
        }
    })
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public configAccessPassword: any;

    public useConfigAccessCredentials: boolean;

    @Metamodel.attribute({
        display: stringResources.SsisActivityEnvrionmentPathLabel,
        association: 'ENVIRONMENT_PATH',
        templatable: true
    })
    public environmentPath?: string;

    @Metamodel.attribute({
        display: stringResources.SsisActivityPackageContentDisplayName,
        association: 'INLINE_PACKAGE_NAME'
    })
    public inlinePackageName?: string;

    @Metamodel.attribute({
        display: stringResources.SsisActivityPackageContentDisplayName,
        association: 'PACKAGE_CONTENT',
        templatable: true,
        constraint: {
            validator: ExecuteSSISPackageActivity.createExecuteSSISPackageContentValidator()
        }
    })
    public packageContent?: string;

    @Metamodel.attribute({
        display: stringResources.SsisActivityPackageLastModifiedDateDisplayName,
        association: 'PACKAGE_LAST_MODIFIED_DATE'
    })
    public packageLastModifiedDate?: string;

    public useChildPackage: boolean;

    @Metamodel.attribute({
        display: stringResources.SsisActivityChildPackagesDisplayName,
        association: 'CHILD_PACKAGES',
        constraint: {
            validator: ExecuteSSISPackageActivity.createExecuteSSISPackageChildPackageConfigValidator()
        }
    })
    public childPackages?: ExecuteSSISPackageChildPackageConfig[];

    @Metamodel.attribute({
        display: stringResources.SsisActivitySsisIrDisplayName,
        association: 'SSIS_IR',
        reference: true,
        type: IntegrationRuntime.MODEL,
        constraint: {
            required: true
        }
    })
    public ssisIr?: IntegrationRuntime;

    @Metamodel.attribute({
        display: stringResources.Folder,
        association: 'SSIS_FOLDER',
        constraint: {
            validator: ExecuteSSISPackageActivity.createSsisFolderValidator()
        }
    })
    public ssisFolder: string;

    @Metamodel.attribute({
        display: stringResources.ProjectLabel,
        association: 'SSIS_PROJECT',
        constraint: {
            validator: ExecuteSSISPackageActivity.createSsisProjectValidator()
        }
    })
    public ssisProject: string;

    @Metamodel.attribute({
        display: stringResources.PackageLabel,
        association: 'SSIS_PACKAGE',
        constraint: {
            validator: ExecuteSSISPackageActivity.createSsisPackageValidator()
        }
    })
    public ssisPackage: string;

    @Metamodel.attribute({
        display: stringResources.EnvironmentLabel,
        association: 'SSIS_ENVIRONMENT'
    })
    public ssisEnvironment?: string;

    @Metamodel.attribute({
        display: stringResources.DomainLabel,
        association: 'LOG_ACCESS_DOMAIN',
        templatable: true,
        constraint: {
            validator: ExecuteSSISPackageActivity.createLogDomainValidator()
        }
    })
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public logAccessDomain: any;

    @Metamodel.attribute({
        display: stringResources.ModelName_UserName,
        association: 'LOG_ACCESS_USERNAME',
        templatable: true,
        constraint: {
            validator: ExecuteSSISPackageActivity.createLogUserNameValidator()
        }
    })
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public logAccessUserName: any;

    @Metamodel.attribute({
        display: stringResources.ModelName_Password,
        association: 'LOG_ACCESS_PASSWORD',
        templatable: true,
        constraint: {
            validator: ExecuteSSISPackageActivity.createLogPasswordValidator()
        }
    })
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public logAccessPassword: any;

    public copyCredentialForLog: boolean;

    public copyCredentialForPackage: boolean;

    constructor(name?: string, description?: string) {
        super(name, description);
        this.activityType = 'ExecutionActivity';
    }

    private static createPackagePathValidator(): (activity?: ExecuteSSISPackageActivity, packagePath?: string, violation?: IViolation) => IViolation[] | undefined {
        return (activity?: ExecuteSSISPackageActivity, packagePath?: string, violation?: IViolation): IViolation[] | undefined => {
            if (!activity || activity.configurationMode === ExecuteSSISPackageActivity.CONFIGURATION_MODE_AUTO || activity.packageLocation !== ExecuteSSISPackageActivity.PACKAGE_LOCATION_SSISDB) {
                return undefined;
            }
            const violations = new Array<IViolation>();
            if (activity.configurationMode === ExecuteSSISPackageActivity.CONFIGURATION_MODE_MANUAL) {
                if (!packagePath) {
                    violations.push({
                        context: activity,
                        message: () => sanitizeAndFormatString(stringResources.RequiredValidatorMessage, stringResources.SsisActivityLabel, '\'' + activity.name + '\'',
                            stringResources.SsisActivityPackagePathLabel),
                        attributes: [ExecuteSSISPackageActivity.PACKAGE_PATH]
                    });
                }
            }
            return violations.length > 0 ? violations : undefined;
        };
    }

    private static createFilePackagePathValidator(): (activity?: ExecuteSSISPackageActivity, filePackagePath?: string, violation?: IViolation) => IViolation[] | undefined {
        return (activity?: ExecuteSSISPackageActivity, filePackagePath?: string, violation?: IViolation): IViolation[] | undefined => {
            if (!activity || activity.packageLocation !== ExecuteSSISPackageActivity.PACKAGE_LOCATION_PACKAGE_FILE) {
                return undefined;
            }
            const violations = new Array<IViolation>();
            if (!filePackagePath) {
                violations.push({
                    context: activity,
                    message: () => sanitizeAndFormatString(stringResources.RequiredValidatorMessage, stringResources.SsisActivityLabel, '\'' + activity.name + '\'',
                        stringResources.SsisActivityPackagePathLabel),
                    attributes: [ExecuteSSISPackageActivity.FILE_PACKAGE_PATH]
                });
            } else if (!filePackagePath.toLowerCase().endsWith(ExecuteSSISPackageActivity.PACKAGE_EXTENSION)) {
                violations.push({
                    context: activity,
                    message: () => sanitizeAndFormatString(stringResources.SsisFilePackagePathValidatorMessage, stringResources.SsisActivityLabel, '\'' + activity.name + '\''),
                    attributes: [ExecuteSSISPackageActivity.FILE_PACKAGE_PATH]
                });
            } else if (!filePackagePath.replace(/\\/g, '/').startsWith('//')) {
                violations.push({
                    context: activity,
                    message: () => sanitizeAndFormatString(stringResources.SsisFilePackagePathFormatValidatorMessage, stringResources.SsisActivityLabel, '\'' + activity.name + '\''),
                    attributes: [ExecuteSSISPackageActivity.FILE_PACKAGE_PATH]
                });
            }
            return violations.length > 0 ? violations : undefined;
        };
    }

    private static createPackageStoreNameValidator(): (activity?: ExecuteSSISPackageActivity, packageStoreName?: string, violation?: IViolation) => IViolation[] | undefined {
        return (activity?: ExecuteSSISPackageActivity, packageStoreName?: string, violation?: IViolation): IViolation[] | undefined => {
            if (!activity || activity.packageLocation !== ExecuteSSISPackageActivity.PACKAGE_LOCATION_PACKAGE_STORE) {
                return undefined;
            }
            const violations = new Array<IViolation>();
            if (stringIsNullOrEmpty(packageStoreName)) {
                violations.push({
                    context: activity,
                    message: () => sanitizeAndFormatString(stringResources.RequiredValidatorMessage, stringResources.SsisActivityLabel, '\'' + activity.name + '\'',
                        stringResources.SsisActivityPackageStoreNameLabel),
                    attributes: [ExecuteSSISPackageActivity.FILE_PACKAGE_PATH]
                });
            } else {
                const ir = activity.ssisIr as ManagedIntegrationRuntime;
                if (ir && !ir.sharedIROrigianlResourceId) {
                    if (!ir.packageStores || ir.packageStores.length === 0) {
                        violations.push({
                            context: activity,
                            message: () => sanitizeAndFormatString(stringResources.SsisPackageStoreNoPackageStoreValidatorMessage, stringResources.SsisActivityLabel, '\'' + activity.name + '\''),
                            attributes: [ExecuteSSISPackageActivity.FILE_PACKAGE_PATH]
                        });
                    } else {
                        const storeExist = ir.packageStores.some((store: PackageStore) => store.storeName === packageStoreName);
                        if (!storeExist) {
                            violations.push({
                                context: activity,
                                message: () => sanitizeAndFormatString(stringResources.SsisFilePackagePathNotExistPackageStoreValidatorMessage, stringResources.SsisActivityLabel,
                                    '\'' + activity.name + '\'', packageStoreName),
                                attributes: [ExecuteSSISPackageActivity.FILE_PACKAGE_PATH]
                            });
                        }
                    }
                }
            }
            return violations.length > 0 ? violations : undefined;
        };
    }

    private static createPackageStorePathValidator(): (activity?: ExecuteSSISPackageActivity, packageStorePath?: string, violation?: IViolation) => IViolation[] | undefined {
        return (activity?: ExecuteSSISPackageActivity, packageStorePath?: string, violation?: IViolation): IViolation[] | undefined => {
            if (!activity || activity.packageLocation !== ExecuteSSISPackageActivity.PACKAGE_LOCATION_PACKAGE_STORE) {
                return undefined;
            }
            const violations = new Array<IViolation>();
            if (stringIsNullOrEmpty(packageStorePath)) {
                violations.push({
                    context: activity,
                    message: () => sanitizeAndFormatString(stringResources.RequiredValidatorMessage, stringResources.SsisActivityLabel, '\'' + activity.name + '\'',
                        stringResources.SsisActivityPackagePathLabel),
                    attributes: [ExecuteSSISPackageActivity.FILE_PACKAGE_PATH]
                });
            } else if (!stringIsNullOrEmpty(ExecuteSSISPackageActivity.PACKAGESTORE_PACKAGEPATH_VALIDATOR(packageStorePath))) {
                violations.push({
                    context: activity,
                    message: () => sanitizeAndFormatString(stringResources.SsisFilePackagePathFormatValidatorMessage, stringResources.SsisActivityLabel, '\'' + activity.name + '\''),
                    attributes: [ExecuteSSISPackageActivity.FILE_PACKAGE_PATH]
                });
            }
            return violations.length > 0 ? violations : undefined;
        };
    }

    private static createSsisFolderValidator(): (activity?: ExecuteSSISPackageActivity, ssisFolder?: string, violation?: IViolation) => IViolation[] | undefined {
        return (activity?: ExecuteSSISPackageActivity, ssisFolder?: string, violation?: IViolation): IViolation[] | undefined => {
            if (!activity || activity.configurationMode === ExecuteSSISPackageActivity.CONFIGURATION_MODE_MANUAL
            || (activity.packageLocation !== ExecuteSSISPackageActivity.PACKAGE_LOCATION_SSISDB)) {
                return undefined;
            }
            const violations = new Array<IViolation>();
            if (activity.configurationMode === ExecuteSSISPackageActivity.CONFIGURATION_MODE_AUTO) {
                if (!ssisFolder) {
                    violations.push({
                        context: activity,
                        message: () => sanitizeAndFormatString(stringResources.RequiredValidatorMessage, stringResources.SsisActivityLabel, '\'' + activity.name + '\'',
                            stringResources.Folder),
                        attributes: [ExecuteSSISPackageActivity.SSIS_PACKAGE]
                    });
                }
            }
            return violations.length > 0 ? violations : undefined;
        };
    }

    private static createSsisProjectValidator(): (activity?: ExecuteSSISPackageActivity, ssisProject?: string, violation?: IViolation) => IViolation[] | undefined {
        return (activity?: ExecuteSSISPackageActivity, ssisProject?: string, violation?: IViolation): IViolation[] | undefined => {
            if (!activity || activity.configurationMode === ExecuteSSISPackageActivity.CONFIGURATION_MODE_MANUAL || activity.packageLocation !== ExecuteSSISPackageActivity.PACKAGE_LOCATION_SSISDB) {
                return undefined;
            }
            const violations = new Array<IViolation>();
            if (activity.configurationMode === ExecuteSSISPackageActivity.CONFIGURATION_MODE_AUTO) {
                if (!ssisProject) {
                    violations.push({
                        context: activity,
                        message: () => sanitizeAndFormatString(stringResources.RequiredValidatorMessage, stringResources.SsisActivityLabel, '\'' + activity.name + '\'',
                            stringResources.ProjectLabel),
                        attributes: [ExecuteSSISPackageActivity.SSIS_PACKAGE]
                    });
                }
            }
            return violations.length > 0 ? violations : undefined;
        };
    }

    private static createSsisPackageValidator(): (activity?: ExecuteSSISPackageActivity, ssisPackage?: string, violation?: IViolation) => IViolation[] | undefined {
        return (activity?: ExecuteSSISPackageActivity, ssisPackage?: string, violation?: IViolation): IViolation[] | undefined => {
            if (!activity || activity.configurationMode === ExecuteSSISPackageActivity.CONFIGURATION_MODE_MANUAL || activity.packageLocation !== ExecuteSSISPackageActivity.PACKAGE_LOCATION_SSISDB) {
                return undefined;
            }
            const violations = new Array<IViolation>();
            if (activity.configurationMode === ExecuteSSISPackageActivity.CONFIGURATION_MODE_AUTO) {
                if (!ssisPackage) {
                    violations.push({
                        context: activity,
                        message: () => sanitizeAndFormatString(stringResources.RequiredValidatorMessage, stringResources.SsisActivityLabel, '\'' + activity.name + '\'',
                            stringResources.PackageLabel),
                        attributes: [ExecuteSSISPackageActivity.SSIS_PACKAGE]
                    });
                }
            }
            return violations.length > 0 ? violations : undefined;
        };
    }

    private static createExecuteSSISPackageContentValidator(): (activity?: ExecuteSSISPackageActivity, content?: string, violation?: IViolation) => IViolation[] | undefined {
        return (activity?: ExecuteSSISPackageActivity, ssisPackage?: string, violation?: IViolation): IViolation[] | undefined => {
            if (!activity || activity.packageLocation !== ExecuteSSISPackageActivity.PACKAGE_LOCATION_INLINE_PACKAGE) {
                return undefined;
            }
            const violations = new Array<IViolation>();
            if (stringIsNullOrEmpty(activity.packageContent) && !ValueAdapter.isTemplated(activity, ExecuteSSISPackageActivity.PACKAGE_CONTENT)) {
                violations.push({
                    context: activity,
                    message: () => sanitizeAndFormatString(stringResources.RequiredValidatorMessage, stringResources.SsisActivityLabel, '\'' + activity.name + '\'',
                        stringResources.SsisActivityInlinePackageLabel),
                    attributes: [ExecuteSSISPackageActivity.PACKAGE_CONTENT]
                });
            } else {
                const activityUsage = collectActivityInlinePackageUsage(activity.owner);
                if (activityUsage.totalSizeInKb > ExecuteSSISPackageActivity.INLINE_PACKAGE_ACTIVITY_MAX_SIZE_KB) {
                    violations.push({
                        context: activity,
                        message: () => format(stringResources.SsisActivityActivityInlinePackageOverSize,
                            ExecuteSSISPackageActivity.INLINE_PACKAGE_ACTIVITY_MAX_SIZE_KB.toString(), activityUsage.totalSizeInKb.toFixed(2)),
                        attributes: [ExecuteSSISPackageActivity.PACKAGE_CONTENT]
                    });
                }
                const parameterUsage = collectPipelineInlinePackageUsage(activity.owner);
                const totalSize = parameterUsage.totalSizeInKb + activityUsage.totalSizeInKb;
                if (totalSize > ExecuteSSISPackageActivity.INLINE_PACKAGE_PIPELINE_MAX_SIZE_KB) {
                    violations.push({
                        context: activity,
                        message: () => format(stringResources.SsisActivityPipelineInlinePackageOverSize,
                            ExecuteSSISPackageActivity.INLINE_PACKAGE_PIPELINE_MAX_SIZE_KB.toString(), totalSize.toFixed(2)),
                        attributes: [ExecuteSSISPackageActivity.PACKAGE_CONTENT]
                    });
                }
            }
            return violations.length > 0 ? violations : undefined;
        };
    }

    private static createExecuteSSISPackageChildPackageConfigValidator(): (activity?: {}, configs?: ExecuteSSISPackageChildPackageConfig[], violation?: IViolation) => IViolation[] | undefined {
        return (activity?: ExecuteSSISPackageActivity, configs?: ExecuteSSISPackageChildPackageConfig[], violation?: IViolation): IViolation[] | undefined => {
            if (!configs || (!activity && activity.packageLocation !== ExecuteSSISPackageActivity.PACKAGE_LOCATION_INLINE_PACKAGE)) {
                return undefined;
            }
            const violations = new Array<IViolation>();
            configs.forEach((config, index) => {
                // validate name
                if (stringIsNullOrEmpty(config.packagePath)) {
                    violations.push({
                        message: () => sanitizeAndFormatString(stringResources.RequiredValidatorMessage, stringResources.SsisActivityLabel, '\'' + activity.name + '\'',
                            stringResources.SsisActivityInlineChildPackagePathLabel),
                        context: {
                            field: 'path',
                            index,
                            attributes: [ExecuteSSISPackageActivity.CHILD_PACKAGES]
                        }
                    });
                }
                if (stringIsNullOrEmpty(config.packageContent) && !ValueAdapter.isTemplated(config, ExecuteSSISPackageChildPackageConfig.PACKAGE_CONTENT)) {
                    violations.push({
                        message: () => sanitizeAndFormatString(stringResources.RequiredValidatorMessage, stringResources.SsisActivityLabel, '\'' + activity.name + '\'',
                            stringResources.SsisActivityInlineChildPackageContentLabel),
                        context: {
                            field: 'content',
                            index,
                            attributes: [ExecuteSSISPackageActivity.CHILD_PACKAGES]
                        }
                    });
                }
            });
            return violations.length > 0 ? violations : undefined;
        };
    }

    private static createWinauthDomainValidator(): (activity?: ExecuteSSISPackageActivity, winauthDomain?: string, violation?: IViolation) => IViolation[] | undefined {
        return (activity?: ExecuteSSISPackageActivity, winauthDomain?: string, violation?: IViolation): IViolation[] | undefined => {
            if (!activity || activity.useWinauth === false) {
                return undefined;
            }
            const violations = new Array<IViolation>();
            if (activity.useWinauth) {
                if (!winauthDomain) {
                    violations.push({
                        context: activity,
                        message: () => sanitizeAndFormatString(stringResources.RequiredValidatorMessage, stringResources.SsisActivityLabel, '\'' + activity.name + '\'',
                            stringResources.DomainLabel),
                        attributes: [ExecuteSSISPackageActivity.WINAUTH_DOMAIN]
                    });
                }
            }
            return violations.length > 0 ? violations : undefined;
        };
    }

    private static createWinauthUserNameValidator(): (activity?: ExecuteSSISPackageActivity, winauthUserName?: string, violation?: IViolation) => IViolation[] | undefined {
        return (activity?: ExecuteSSISPackageActivity, winauthUserName?: string, violation?: IViolation): IViolation[] | undefined => {
            if (!activity || activity.useWinauth === false) {
                return undefined;
            }
            const violations = new Array<IViolation>();
            if (activity.useWinauth) {
                if (!winauthUserName) {
                    violations.push({
                        context: activity,
                        message: () => sanitizeAndFormatString(stringResources.RequiredValidatorMessage, stringResources.SsisActivityLabel, '\'' + activity.name + '\'',
                            stringResources.ModelName_UserName),
                        attributes: [ExecuteSSISPackageActivity.WINAUTH_USERNAME]
                    });
                }
            }
            return violations.length > 0 ? violations : undefined;
        };
    }

    private static createWinauthPasswordValidator(): (activity?: ExecuteSSISPackageActivity, winauthPassword?: string, violation?: IViolation) => IViolation[] | undefined {
        return (activity?: ExecuteSSISPackageActivity, winauthPassword?: string, violation?: IViolation): IViolation[] | undefined => {
            if (!activity || activity.useWinauth === false) {
                return undefined;
            }
            const violations = new Array<IViolation>();
            if (activity.useWinauth) {
                if (!winauthPassword) {
                    violations.push({
                        context: activity,
                        message: () => sanitizeAndFormatString(stringResources.RequiredValidatorMessage, stringResources.SsisActivityLabel, '\'' + activity.name + '\'',
                            stringResources.ModelName_Password),
                        attributes: [ExecuteSSISPackageActivity.WINAUTH_PASSWORD]
                    });
                }
            }
            return violations.length > 0 ? violations : undefined;
        };
    }

    private static createPackageDomainValidator(): (activity?: ExecuteSSISPackageActivity, packageAccessDomain?: string, violation?: IViolation) => IViolation[] | undefined {
        return (activity?: ExecuteSSISPackageActivity, packageAccessDomain?: string, violation?: IViolation): IViolation[] | undefined => {
            if (!activity || activity.packageLocation === ExecuteSSISPackageActivity.PACKAGE_LOCATION_SSISDB
                || activity.packageLocation === ExecuteSSISPackageActivity.PACKAGE_LOCATION_INLINE_PACKAGE
                || (activity.packageLocation === ExecuteSSISPackageActivity.PACKAGE_LOCATION_SQL_SERVER && (!activity.configFile || activity.configFile.length === 0))
                || activity.packageLocation === ExecuteSSISPackageActivity.PACKAGE_LOCATION_PACKAGE_STORE) {
                return undefined;
            }
            const violations = new Array<IViolation>();
            if (activity.packageLocation !== ExecuteSSISPackageActivity.PACKAGE_LOCATION_SSISDB && !activity.copyCredentialForPackage) {
                if (!packageAccessDomain) {
                    violations.push({
                        context: activity,
                        message: () => sanitizeAndFormatString(stringResources.RequiredValidatorMessage, stringResources.SsisActivityLabel, '\'' + activity.name + '\'',
                            stringResources.DomainLabel),
                        attributes: [ExecuteSSISPackageActivity.PACKAGE_ACCESS_DOMAIN]
                    });
                }
            }
            return violations.length > 0 ? violations : undefined;
        };
    }

    private static createPackageUserNameValidator(): (activity?: ExecuteSSISPackageActivity, packageAccessUserName?: string, violation?: IViolation) => IViolation[] | undefined {
        return (activity?: ExecuteSSISPackageActivity, packageAccessUserName?: string, violation?: IViolation): IViolation[] | undefined => {
            if (!activity || activity.packageLocation === ExecuteSSISPackageActivity.PACKAGE_LOCATION_SSISDB
                || activity.packageLocation === ExecuteSSISPackageActivity.PACKAGE_LOCATION_INLINE_PACKAGE
                || (activity.packageLocation === ExecuteSSISPackageActivity.PACKAGE_LOCATION_SQL_SERVER && (!activity.configFile || activity.configFile.length === 0))
                || activity.packageLocation === ExecuteSSISPackageActivity.PACKAGE_LOCATION_PACKAGE_STORE) {
                return undefined;
            }
            const violations = new Array<IViolation>();
            if (activity.packageLocation !== ExecuteSSISPackageActivity.PACKAGE_LOCATION_SSISDB && !activity.copyCredentialForPackage) {
                if (!packageAccessUserName) {
                    violations.push({
                        context: activity,
                        message: () => sanitizeAndFormatString(stringResources.RequiredValidatorMessage, stringResources.SsisActivityLabel, '\'' + activity.name + '\'',
                            stringResources.ModelName_UserName),
                        attributes: [ExecuteSSISPackageActivity.PACKAGE_ACCESS_USERNAME]
                    });
                }
            }
            return violations.length > 0 ? violations : undefined;
        };
    }

    private static createPackagePasswordValidator(): (activity?: ExecuteSSISPackageActivity, packageAccessPassword?: string, violation?: IViolation) => IViolation[] | undefined {
        return (activity?: ExecuteSSISPackageActivity, packageAccessPassword?: string, violation?: IViolation): IViolation[] | undefined => {
            if (!activity || activity.packageLocation === ExecuteSSISPackageActivity.PACKAGE_LOCATION_SSISDB
                || activity.packageLocation === ExecuteSSISPackageActivity.PACKAGE_LOCATION_INLINE_PACKAGE
                || (activity.packageLocation === ExecuteSSISPackageActivity.PACKAGE_LOCATION_SQL_SERVER && (!activity.configFile || activity.configFile.length === 0))
                || activity.packageLocation === ExecuteSSISPackageActivity.PACKAGE_LOCATION_PACKAGE_STORE) {
                return undefined;
            }
            const violations = new Array<IViolation>();
            if (activity.packageLocation !== ExecuteSSISPackageActivity.PACKAGE_LOCATION_SSISDB && !activity.copyCredentialForPackage) {
                if (!packageAccessPassword) {
                    violations.push({
                        context: activity,
                        message: () => sanitizeAndFormatString(stringResources.RequiredValidatorMessage, stringResources.SsisActivityLabel, '\'' + activity.name + '\'',
                            stringResources.ModelName_Password),
                        attributes: [ExecuteSSISPackageActivity.PACKAGE_ACCESS_PASSWORD]
                    });
                }
            }
            return violations.length > 0 ? violations : undefined;
        };
    }

    private static createConfigDomainValidator(): (activity?: ExecuteSSISPackageActivity, configAccessDomain?: string, violation?: IViolation) => IViolation[] | undefined {
        return (activity?: ExecuteSSISPackageActivity, configAccessDomain?: string, violation?: IViolation): IViolation[] | undefined => {
            if (!activity || activity.packageLocation !== ExecuteSSISPackageActivity.PACKAGE_LOCATION_PACKAGE_STORE) {
                return undefined;
            }
            const violations = new Array<IViolation>();
            if (activity.packageLocation === ExecuteSSISPackageActivity.PACKAGE_LOCATION_PACKAGE_STORE && !activity.copyCredentialForPackage) {
                const ir = activity.ssisIr as ManagedIntegrationRuntime;
                let isRequired = false;
                if (ir && activity.configFile) {
                    const store = ir.packageStores.find((store: PackageStore) => store.storeName === activity.packageStoreName);
                    if (store) {
                        isRequired = store.linkedService instanceof AzureSqlMILinkedService;
                    }
                }
                if ((activity.useConfigAccessCredentials || isRequired) && !configAccessDomain) {
                    violations.push({
                        context: activity,
                        message: () => sanitizeAndFormatString(stringResources.RequiredValidatorMessage, stringResources.SsisActivityLabel, '\'' + activity.name + '\'',
                            stringResources.DomainLabel),
                        attributes: [ExecuteSSISPackageActivity.CONFIG_ACCESS_DOMAIN]
                    });
                }
            }
            return violations.length > 0 ? violations : undefined;
        };
    }

    private static createConfigUserNameValidator(): (activity?: ExecuteSSISPackageActivity, configAccessUserName?: string, violation?: IViolation) => IViolation[] | undefined {
        return (activity?: ExecuteSSISPackageActivity, configAccessUserName?: string, violation?: IViolation): IViolation[] | undefined => {
            if (!activity || activity.packageLocation !== ExecuteSSISPackageActivity.PACKAGE_LOCATION_PACKAGE_STORE) {
                return undefined;
            }
            const violations = new Array<IViolation>();
            if (activity.packageLocation === ExecuteSSISPackageActivity.PACKAGE_LOCATION_PACKAGE_STORE && !activity.copyCredentialForPackage) {
                const ir = activity.ssisIr as ManagedIntegrationRuntime;
                let isRequired = false;
                if (ir && activity.configFile) {
                    const store = ir.packageStores.find((store: PackageStore) => store.storeName === activity.packageStoreName);
                    if (store) {
                        isRequired = store.linkedService instanceof AzureSqlMILinkedService;
                    }
                }
                if ((activity.useConfigAccessCredentials || isRequired) && !configAccessUserName) {
                    violations.push({
                        context: activity,
                        message: () => sanitizeAndFormatString(stringResources.RequiredValidatorMessage, stringResources.SsisActivityLabel, '\'' + activity.name + '\'',
                            stringResources.ModelName_UserName),
                        attributes: [ExecuteSSISPackageActivity.CONFIG_ACCESS_USERNAME]
                    });
                }
            }
            return violations.length > 0 ? violations : undefined;
        };
    }

    private static createConfigPasswordValidator(): (activity?: ExecuteSSISPackageActivity, configAccessPassword?: string, violation?: IViolation) => IViolation[] | undefined {
        return (activity?: ExecuteSSISPackageActivity, configAccessPassword?: string, violation?: IViolation): IViolation[] | undefined => {
            if (!activity || activity.packageLocation !== ExecuteSSISPackageActivity.PACKAGE_LOCATION_PACKAGE_STORE) {
                return undefined;
            }
            const violations = new Array<IViolation>();
            if (activity.packageLocation === ExecuteSSISPackageActivity.PACKAGE_LOCATION_PACKAGE_STORE && !activity.copyCredentialForPackage) {
                const ir = activity.ssisIr as ManagedIntegrationRuntime;
                let isRequired = false;
                if (ir && activity.configFile) {
                    const store = ir.packageStores.find((store: PackageStore) => store.storeName === activity.packageStoreName);
                    if (store) {
                        isRequired = store.linkedService instanceof AzureSqlMILinkedService;
                    }
                }
                if ((activity.useConfigAccessCredentials || isRequired) && !configAccessPassword) {
                    violations.push({
                        context: activity,
                        message: () => sanitizeAndFormatString(stringResources.RequiredValidatorMessage, stringResources.SsisActivityLabel, '\'' + activity.name + '\'',
                            stringResources.ModelName_Password),
                        attributes: [ExecuteSSISPackageActivity.CONFIG_ACCESS_PASSWORD]
                    });
                }
            }
            return violations.length > 0 ? violations : undefined;
        };
    }

    private static createLogDomainValidator(): (activity?: ExecuteSSISPackageActivity, logAccessDomain?: string, violation?: IViolation) => IViolation[] | undefined {
        return (activity?: ExecuteSSISPackageActivity, logAccessDomain?: string, violation?: IViolation): IViolation[] | undefined => {
            if (!activity || activity.packageLocation === ExecuteSSISPackageActivity.PACKAGE_LOCATION_SSISDB) {
                return undefined;
            }
            const violations = new Array<IViolation>();
            if ((activity.packageLocation !== ExecuteSSISPackageActivity.PACKAGE_LOCATION_SSISDB && !activity.copyCredentialForLog && activity.logPath)
                || (activity.packageLocation === ExecuteSSISPackageActivity.PACKAGE_LOCATION_INLINE_PACKAGE && activity.logPath)) {
                if (!logAccessDomain) {
                    violations.push({
                        context: activity,
                        message: () => sanitizeAndFormatString(stringResources.RequiredValidatorMessage, stringResources.SsisActivityLabel, '\'' + activity.name + '\'',
                            stringResources.DomainLabel),
                        attributes: [ExecuteSSISPackageActivity.LOG_ACCESS_DOMAIN]
                    });
                }
            }
            return violations.length > 0 ? violations : undefined;
        };
    }

    private static createLogUserNameValidator(): (activity?: ExecuteSSISPackageActivity, logAccessUserName?: string, violation?: IViolation) => IViolation[] | undefined {
        return (activity?: ExecuteSSISPackageActivity, logAccessUserName?: string, violation?: IViolation): IViolation[] | undefined => {
            if (!activity || activity.packageLocation === ExecuteSSISPackageActivity.PACKAGE_LOCATION_SSISDB) {
                return undefined;
            }
            const violations = new Array<IViolation>();
            if (activity.packageLocation !== ExecuteSSISPackageActivity.PACKAGE_LOCATION_SSISDB && !activity.copyCredentialForLog && activity.logPath
                || (activity.packageLocation === ExecuteSSISPackageActivity.PACKAGE_LOCATION_INLINE_PACKAGE && activity.logPath)) {
                if (!logAccessUserName) {
                    violations.push({
                        context: activity,
                        message: () => sanitizeAndFormatString(stringResources.RequiredValidatorMessage, stringResources.SsisActivityLabel, '\'' + activity.name + '\'',
                            stringResources.ModelName_UserName),
                        attributes: [ExecuteSSISPackageActivity.LOG_ACCESS_USERNAME]
                    });
                }
            }
            return violations.length > 0 ? violations : undefined;
        };
    }

    private static createLogPasswordValidator(): (activity?: ExecuteSSISPackageActivity, logAccessPassword?: string, violation?: IViolation) => IViolation[] | undefined {
        return (activity?: ExecuteSSISPackageActivity, logAccessPassword?: string, violation?: IViolation): IViolation[] | undefined => {
            if (!activity || activity.packageLocation === ExecuteSSISPackageActivity.PACKAGE_LOCATION_SSISDB) {
                return undefined;
            }
            const violations = new Array<IViolation>();
            if (activity.packageLocation !== ExecuteSSISPackageActivity.PACKAGE_LOCATION_SSISDB && !activity.copyCredentialForLog && activity.logPath
                || (activity.packageLocation === ExecuteSSISPackageActivity.PACKAGE_LOCATION_INLINE_PACKAGE && activity.logPath)) {
                if (!logAccessPassword) {
                    violations.push({
                        context: activity,
                        message: () => sanitizeAndFormatString(stringResources.RequiredValidatorMessage, stringResources.SsisActivityLabel, '\'' + activity.name + '\'',
                            stringResources.ModelName_Password),
                        attributes: [ExecuteSSISPackageActivity.LOG_ACCESS_PASSWORD]
                    });
                }
            }
            return violations.length > 0 ? violations : undefined;
        };
    }

    private static createSqlServerLinkedServiceValidator(): (activity?: ExecuteSSISPackageActivity, linkedService?: AzureSqlMILinkedService, violation?: IViolation) => IViolation[] {
        return (activity?: ExecuteSSISPackageActivity, linkedService?: AzureSqlMILinkedService, violation?: IViolation): IViolation[] | undefined => {
            if (!activity || activity.packageLocation !== ExecuteSSISPackageActivity.PACKAGE_LOCATION_SQL_SERVER) {
                return undefined;
            }
            const violations = new Array<IViolation>();
            if (activity.packageLocation === ExecuteSSISPackageActivity.PACKAGE_LOCATION_SQL_SERVER) {
                if (!activity.sqlServerLinkedService) {
                    violations.push({
                        context: activity,
                        message: () => sanitizeAndFormatString(stringResources.RequiredValidatorMessage, stringResources.SsisActivityLabel, '\'' + activity.name + '\'',
                            stringResources.ModelName_SqlServerLinkedService),
                            attributes: [ExecuteSSISPackageActivity.SQLSERVER_LINKEDSERVICE]
                    });
                }
            }
            return violations.length > 0 ? violations : undefined;
        };
    }

}

export interface IActivityInlinePackageUsage {
    totalSizeInKb: number;
    maxSizeInKb: number;
    activityNumber: number;
    packageNumber: number;
}

export interface IPipelineInlinePackageUsage {
    totalSizeInKb: number;
    maxSizeInKb: number;
    parameterNumber: number;
    packageParameterNumber: number;
}

export function collectPipelineInlinePackageUsage(pipeline: Pipeline): IPipelineInlinePackageUsage {
    let totalSize = 0;
    let maxSize = 0;
    let parameterNum = 0;
    let packageParameterNum = 0;
    if (pipeline && pipeline.parameters) {
        pipeline.parameters.forEach((parameter) => {
            parameterNum++;
            if (parameter.defaultValue) {
                if (parameter.defaultValue.startsWith(ExecuteSSISPackageActivity.INLINE_PACKAGE_COMPRESSION_HEADER_V1)) {
                    packageParameterNum++;
                }
                const curSize = Buffer.from(parameter.defaultValue).length;
                totalSize += curSize;
                maxSize = curSize > maxSize ? curSize : maxSize;
            }
        });
    }
    return {
        totalSizeInKb: totalSize / 1024,
        maxSizeInKb: maxSize / 1024,
        parameterNumber: parameterNum,
        packageParameterNumber: packageParameterNum
    };
}

export function collectActivityInlinePackageUsage(pipeline: Pipeline): IActivityInlinePackageUsage {
    let totalSize = 0;
    let maxSize = 0;
    let activityNum = 0;
    let packageNum = 0;
    if (pipeline && pipeline.activities) {
        pipeline.activities.forEach((activity) => {
            if (activity instanceof ExecuteSSISPackageActivity) {
                activityNum++;
                if (activity.packageContent) {
                    const curSize = Buffer.from(activity.packageContent).length;
                    maxSize = curSize > maxSize ? curSize : maxSize;
                    totalSize += curSize;
                }
                if (ValueAdapter.toResource(activity, ExecuteSSISPackageActivity.INLINE_PACKAGE_NAME)) {
                    packageNum++;
                }
                const childPackages = (activity.useChildPackage && activity.childPackages) ? activity.childPackages : [];
                const childPackageSize = childPackages.map((pkg) => {
                    if (pkg.packageContent) {
                        packageNum++;
                        const curSize = Buffer.from(pkg.packageContent).length;
                        maxSize = curSize > maxSize ? curSize : maxSize;
                        return curSize;
                    }
                    return 0;
                }).reduce((prev, len) => prev + len, 0);
                totalSize += childPackageSize;
            }
        });
    }
    return {
        totalSizeInKb: totalSize / 1024,
        maxSizeInKb: maxSize / 1024,
        activityNumber: activityNum,
        packageNumber: packageNum
    };
}

export function validateActivityInlinePackageSize(usage: IActivityInlinePackageUsage): string {
    if (usage.totalSizeInKb > ExecuteSSISPackageActivity.INLINE_PACKAGE_ACTIVITY_MAX_SIZE_KB) {
        return format(stringResources.SsisActivityActivityInlinePackageOverSize,
            ExecuteSSISPackageActivity.INLINE_PACKAGE_ACTIVITY_MAX_SIZE_KB.toString(), usage.totalSizeInKb.toFixed(2));
    }
    return undefined;
}

export function validatePipelineInlinePackageSize(activityUsage: IActivityInlinePackageUsage, parameterUsage: IPipelineInlinePackageUsage): string {
    const curTotalSize = parameterUsage.totalSizeInKb + activityUsage.totalSizeInKb;
    if (curTotalSize > ExecuteSSISPackageActivity.INLINE_PACKAGE_PIPELINE_MAX_SIZE_KB) {
        return format(stringResources.SsisActivityPipelineInlinePackageOverSize,
            ExecuteSSISPackageActivity.INLINE_PACKAGE_PIPELINE_MAX_SIZE_KB.toString(), curTotalSize.toFixed(2));
    }
    return undefined;
}
